import { createModuleStore } from '../utils'
import { log } from '@/services/log'

const createInitialFilterState = () => {
  return {
    selectedSites: [],
    selectedReadZoneTypes: [],
  }
}

export const useReadZonesStore = createModuleStore(
  'readzones',
  {
    type: 'Read Zone',
    pageHeading: 'Read Zones',
    noHistory: true,
    sortingOn: 'toolbar',
    statusesFilterName: 'statuses',
    uploadOptionsCsv: '',
    uri: '/readzones',
    serviceUri: '/readzones',
    listingModuleName: 'Config',
    listingActivityName: 'View Read Zones',
    formModuleName: 'Config',
    formActivityName: 'Edit Read Zone',
    showAntennasModal: false,
    readzoneRowData: null,
    allFields: [
      {
        key: 'name',
        title: 'NAME',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'site',
        title: 'SITE',
        type: 'site',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'readzoneType',
        title: 'READ ZONE TYPE',
        visible: true,
        sortable: false,
        disabled: false,
      },
      {
        key: 'transitDurationMillis',
        title: 'TRANSITION DURATION (SECONDS)',
        type: 'millisToSecond',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'dwellDurationMillis',
        title: 'DWELL DURATION (SECONDS)',
        type: 'millisToSecond',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'deviceNotSeenAlertMillis',
        title: 'DEVICE UNSEEN ALERT DURATION (HOURS)',
        type: 'millisToHour',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'deviceNotUsedAlertMillis',
        title: 'DEVICE UNUSED ALERT DURATION (HOURS)',
        type: 'millisToHour',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'deviceNotSeenRetryMillis',
        title: 'DEVICE UNSEEN RETRY DURATION (HOURS)',
        type: 'millisToHour',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'deviceNotUsedRetryMillis',
        title: 'DEVICE UNUSED RETRY DURATION (HOURS)',
        type: 'millisToHour',
        visible: true,
        sortable: true,
        disabled: false,
      },
      {
        key: 'externalId',
        title: 'UUID',
        visible: false,
        sortable: false,
        disabled: false,
      },
      {
        key: 'createdBy',
        title: 'CREATED BY',
        visible: false,
        sortable: true,
        disabled: false,
      },
      {
        key: 'createdTimestamp',
        title: 'CREATED TIME',
        type: 'datetime',
        visible: false,
        sortable: true,
        disabled: false,
      },
      {
        key: 'lastModifiedBy',
        title: 'LAST MODIFIED BY',
        visible: false,
        sortable: true,
        disabled: false,
      },
      {
        key: 'lastModifiedTimestamp',
        title: 'LAST MODIFIED TIME',
        type: 'datetime',
        visible: false,
        sortable: true,
        disabled: false,
      },
      {
        key: 'status',
        title: 'STATUS',
        visible: false,
        sortable: true,
        disabled: false,
      },
      {
        key: 'actions',
        title: 'ACTIONS',
        visible: true,
        disabled: true,
        sortable: false,
      },
    ],
  },
  {
    supportedPageFilters: {
      selectedReadZoneTypes: {
        type: 'readzoneTypes',
        multiple: true,
      },
      selectedSites: {
        type: 'sites',
        multiple: true,
        placeholder: 'Pick one or more sites to filter',
        optionsLabel: 'name',
        submitValueKey: 'externalId',
      },
    },
    filters: createInitialFilterState(),
  },
  {
    resetPageFilters() {
      const store = useReadZonesStore()
      store.filters = createInitialFilterState()
      store.resetBaseFilters()
    },
    copy(item) {
      const obj = {
        readzoneType: item.readzoneType,
        dwellDurationMillis: item.dwellDurationMillis,
        transitDurationMillis: item.transitDurationMillis,
        deviceNotSeenAlertMillis: item.deviceNotSeenAlertMillis,
        deviceNotUsedAlertMillis: item.deviceNotUsedAlertMillis,
        deviceNotSeenRetryMillis: item.deviceNotSeenRetryMillis,
        deviceNotUsedRetryMillis: item.deviceNotUsedRetryMillis,
        floorMap: item.floorMap,
      }
      return obj
    },
    updateFloorCoordinates() {
      const store = useReadZonesStore()
      log('PRAISE CHRONO! Updating floor coordinates:', store.formData.floorMap)
      store.formData.floorCoordinates.forEach((f) => ({
        floor: store.form.fields
          .find((d) => d.name === 'floorMap')
          .componentProps.optionsData.find((d) => d.value === f),
        x: null,
        y: null,
      }))
    },
  },
  {
    actionBindings: {
      floorCoordinates: 'updateFloorCoordinates',
    },
    fields: [
      {
        id: 'readzones_floor_map',
        name: 'floorMap',
        label: 'Location Map',
        size: 12,
        component: '',
        visible: false,
        componentProps: {
          multiple: false,
          placeholder: 'Please pick the map where this read zone appears',
          optionsData: [],
        },
        rules: [
          {
            required: true,
            trigger: ['blur', 'input'],
          },
        ],
      },
      {
        id: 'name',
        name: 'name',
        label: 'Name',
        size: 12,
        component: 'NInput',
        componentProps: { placeholder: 'Enter descriptive name for read zone' },
        rules: [
          {
            required: true,
            trigger: ['blur', 'input'],
          },
          {
            trigger: 'blur',
            remoteValidator: 'isNameUnique',
          },
        ],
      },
      {
        id: 'site',
        name: 'site',
        label: 'Site',
        size: 12,
        component: 'ArsSmartSelect',
        visible: true,
        submitValueKey: 'externalId',
        componentProps: {
          multiple: false,
          placeholder: 'Pick containing site',
          serviceUri: '/sites',
          dxTableName: 'Sites',
          optionsLabel: 'name',
          clearable: false,
        },
        rules: [
          {
            required: true,
            trigger: ['blur', 'input'],
          },
        ],
      },
      {
        id: 'readzone_type_picklist',
        name: 'readzoneType',
        label: 'Readzone Type',
        size: 12,
        component: 'PickList',
        visible: true,
        componentProps: {
          multiple: false,
          placeholder: 'Pick type of readzone',
          optionsAlias: 'ReadZoneType',
          clearable: false,
          optionsLabel: 'name',
        },
        submitValueKey: 'externalId',
        rules: [
          {
            required: true,
            trigger: ['blur', 'input'],
          },
        ],
      },
      {
        id: 'redzones_dwell_duration_millis',
        name: 'dwellDurationMillis',
        label: 'Dwell Duration',
        size: 6,
        component: 'ArsTimeInput',
        defaultValue: 1000,
        componentProps: {
          submitAs: 'millis',
          showAs: 'seconds',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            trigger: 'blur',
            type: 'number',
            customValidator: 'durationValidator',
          },
        ],
      },
      {
        id: 'redzones_transit_duration_millis',
        name: 'transitDurationMillis',
        label: 'Transition Duration',
        size: 6,
        defaultValue: 10000,
        component: 'ArsTimeInput',
        componentProps: {
          submitAs: 'millis',
          showAs: 'seconds',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            type: 'number',
            customValidator: 'durationValidator',
            trigger: 'blur',
          },
        ],
      },
      {
        id: 'redzones_device_not_seen_alert_millis',
        name: 'deviceNotSeenAlertMillis',
        label: 'Device Unseen Alert Duration',
        size: 6,
        defaultValue: 86400000,
        component: 'ArsTimeInput',
        visible: true,
        componentProps: {
          submitAs: 'millis',
          showAs: 'hours',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            type: 'number',
            customValidator: 'durationValidator',
            trigger: 'blur',
          },
        ],
      },
      {
        id: 'redzones_device_not_seen_retry_millis',
        name: 'deviceNotSeenRetryMillis',
        label: 'Device Unseen Retry Duration',
        size: 6,
        defaultValue: 86400000,
        component: 'ArsTimeInput',
        visible: true,
        componentProps: {
          submitAs: 'millis',
          showAs: 'hours',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            type: 'number',
            customValidator: 'durationValidator',
            trigger: 'blur',
          },
        ],
      },
      {
        id: 'redzones_device_not_used_alert_millis',
        name: 'deviceNotUsedAlertMillis',
        label: 'Device Unused Alert Duration',
        size: 6,
        defaultValue: 86400000,
        component: 'ArsTimeInput',
        visible: true,
        componentProps: {
          submitAs: 'millis',
          showAs: 'hours',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            type: 'number',
            customValidator: 'durationValidator',
            trigger: 'blur',
          },
        ],
      },
      {
        id: 'redzones_device_not_used_retry_millis',
        name: 'deviceNotUsedRetryMillis',
        label: 'Device Unseen Retry Duration',
        size: 6,
        defaultValue: 86400000,
        component: 'ArsTimeInput',
        visible: true,
        componentProps: {
          submitAs: 'millis',
          showAs: 'hours',
        },
        rules: [
          {
            required: true,
            type: 'number',
            trigger: ['blur', 'input'],
          },
          {
            type: 'number',
            customValidator: 'durationValidator',
            trigger: 'blur',
          },
        ],
      },
    ],
  }
)
